import { Server } from './server';
import { Service, ServiceResponse } from './service';

const MAX_POSTS_IN_CACHE = 100;
const ACTIVITY_POSTS_CACHE_EXPIRATION = 60 * 60 * 24;
const ACTIVITY_METHOD = 'activity/v3';
const PUBLIC_METHOD = 'public/v3';

export class ActivityService extends Service {
  protected unreadCount: number;
  protected newestPost: any;
  protected oldestPost: any;
  protected lastCacheUpdate: Date;

  constructor() {
    super();
    this.unreadCount = 0;
    this.newestPost = null;
    this.oldestPost = null;
    this.lastCacheUpdate = null;
  }

  public async init(): Promise<ServiceResponse> {
    await Server.database.purge('ACTIVITYPOSTS', ACTIVITY_POSTS_CACHE_EXPIRATION);
    await Server.database.purge('ACTIVITYPOST#', ACTIVITY_POSTS_CACHE_EXPIRATION);
    await Server.database.purge('ACTIVITYPOSTREPLIES#', ACTIVITY_POSTS_CACHE_EXPIRATION);
    return {success: true};
  }

  public getUnreadPostCount(): number {
    return this.unreadCount;
  }

  public getOldestPostDate() {
    if(this.oldestPost)
      return new Date(this.oldestPost.date);
    return null;
  }

  // posts cache
  public async setPostsCache(posts:any[], update:boolean = false) {
    await Server.database.putItem(`ACTIVITYPOSTS`, posts, update);
  }

  public async getPostsCache() {
    return await Server.database.getItem(`ACTIVITYPOSTS`, ACTIVITY_POSTS_CACHE_EXPIRATION);
  }

  public async updatePostsCache(post:any) {
    let posts = await this.getPostsCache();
    let postIndex = posts.findIndex(p => p.id == post.id);
    if(postIndex != -1)
      posts[postIndex] = post;
    await this.setPostsCache(posts);
  }

  public async clearPostsCache() {
    await Server.database.removeItem(`ACTIVITYPOSTS`);
  }

  // post cache
  public async setPostInCache(post:any) {
    await Server.database.putItem(`ACTIVITYPOST#${post.id}`, post);
  }

  public async getPostFromCache(id:string) {
    return await Server.database.getItem(`ACTIVITYPOST#${id}`, ACTIVITY_POSTS_CACHE_EXPIRATION);
  }

  public async clearPostFromCache(id:string) {
    await Server.database.removeItem(`ACTIVITYPOST#${id}`);
  }

  // post replies cache
  public async setPostRepliesInCache(id:string, sort:boolean, cache:any) {
    await Server.database.putItem(`ACTIVITYPOSTREPLIES#${id}#${sort}`, cache);
  }

  public async getPostRepliesFromCache(id:string, sort:boolean) {
    return await Server.database.getItem(`ACTIVITYPOSTREPLIES#${id}#${sort}`, ACTIVITY_POSTS_CACHE_EXPIRATION);
  }

  public async updatePostReplyInCache(id:string, reply:any) {
    for(let sort of [true, false]) {
      let cache  = await this.getPostRepliesFromCache(id, sort);
      let replies = cache.replies;
      let replyIndex = replies.findIndex(r => r.date == reply.date && r.user == reply.user);
      if(replyIndex != -1) {
        replies[replyIndex] = reply;
        await this.setPostRepliesInCache(id, sort, {replies});
      }
    }
  }

  public async clearPostRepliesCache(id:string) {
    await Server.database.removeItem(`ACTIVITYPOSTREPLIES#${id}#true`);
    await Server.database.removeItem(`ACTIVITYPOSTREPLIES#${id}#false`);
  }

  // post cache helper
  public async updatePostInCaches(post:any) {
    await this.updatePostsCache(post);
    await this.setPostInCache(post);
  }

  public async clearPostInCaches(id:string) {
    await this.clearPostFromCache(id);
    await this.clearPostRepliesCache(id);
  }
  
  // server commands
  protected async sendActivityCommand(command:string, params:any, data?:string[]) {
    let response = await this.sendCommand(ACTIVITY_METHOD, command, params);
    if(response.status != 200)
      return {success: false, message: response.body.message};
    let ret:any = {success: true};
    if(data) {
      for(let i = 0; i < data.length; i++)
        ret[data[i]] = response.body[data[i]];
    }
    return ret;
  }
    
  public async createPost(content:string) {
    return await this.sendActivityCommand('create-post', {content}, ['id']);
  }

  public async editPost(params: {id:string, date:string, content:string}) {
    return await this.sendActivityCommand('edit-post', params);
  }

  public async deletePost(id:string) {
    return await this.sendActivityCommand('delete-post', {id});
  }

  public async likePost(id:string) {
    return await this.sendActivityCommand('like-post', {id});
  }

  public async unlikePost(id:string) {
    return await this.sendActivityCommand('unlike-post', {id});
  }

  public async getPost(id:string) {
    let method = Server.isLoggedIn() ? ACTIVITY_METHOD : PUBLIC_METHOD;
    let command = Server.isLoggedIn() ? 'get-post' : 'get-activity-post';
    let response = await this.sendCommand(method, command, {id});
    if(response.status != 200)
      return {success: false, message: response.body.message};
    return {success: true, post: response.body.post};
  }

  public async getPostReplies(params:{id:string, sort:boolean, more?:any, after?:any}) {
    let method = Server.isLoggedIn() ? ACTIVITY_METHOD : PUBLIC_METHOD;
    let command = Server.isLoggedIn() ? 'get-post-replies' : 'get-activity-post-replies';
    let response = await this.sendCommand(method, command, params);
    if(response.status != 200)
      return {success: false, message: response.body.message};
    return {success: true, replies: response.body.replies, more: response.body.more};
  }

  public async getPosts(params:{author?:string, filter?:string, before?:any, after?:any} = {}) {
    let method = Server.isLoggedIn() ? ACTIVITY_METHOD : PUBLIC_METHOD;
    let command = Server.isLoggedIn() ? 'get-posts' : 'get-activity-posts';
    let response = await this.sendCommand(method, command, params);
    if(response.status != 200)
      return {success: false, message: response.body.message};
    return {success: true, posts: response.body.posts};
  }

  public async createReply(params:{id:string, content:string}) {
    return await this.sendActivityCommand('create-reply', params, ['date']);
  }

  public async deleteReply(params:{id:string, date:string}) {
    return await this.sendActivityCommand('delete-reply', params);
  }

  public async editReply(params:{id:string, date:string, content:string}) {
    return await this.sendActivityCommand('edit-reply', params);
  }

  public async getLatestPostDate() {
    return await this.sendActivityCommand('get-latest-post-date', {}, ['date']);
  }

  public async convertPostToForumTopic(params:{id:string, forum:string, topic: string}) {
    return await this.sendActivityCommand('convert-post-to-forum-topic', params);
  }
}